import { useState, useRef } from 'react';
import LabelPreview from './LabelPreview';
import { hasCustomFeature } from '../services/customFeatures';
import {
  buildZplLines, beispielPaket, parseLabelSize, parseLabelZeilen, formatLabelZeilen, parseLabelRand, formatLabelRand,
  LABEL_KOEPFE, LABEL_ZEILEN, LABEL_FUSS, LABEL_MM, LABEL_RAND,
  parseLabelCode, formatLabelCode, LABEL_CODE_ARTEN, LABEL_CODE_GROESSEN,
  parseLabelGruss, LABEL_GRUSS_MAX, parseLabelGrafik, formatLabelGrafik, LABEL_THEMEN, bildZuLabelGrafik,
  parseLabelQrUrl, LABEL_QR_URL_MAX, parseLabelQrText, LABEL_QR_TEXT_MAX,
} from '../services/printerService';

/**
 * 🎨 Etikett gestalten: Größe (auch frei in Millimetern), Vorlage, Inhalt, Rahmen, Grußtext & Motiv/Bild — mit Vorschau.
 * Gesteuert: `value` = Etikett-Einstellungen, `onChange(neu)` bei jeder Änderung. Gespeichert wird
 * vom Aufrufer (Drucker-Dialog: sofort am Gerät; Setup: erst am Ende).
 *
 * value: { lang, layout, size, rahmen, kopf, zeilen: [..], dienst, rand: {oben, unten, seite}, vintedNr, code, gruss, grafik, qrUrl, qrText }
 * rand (Ränder in mm) gehört zum Drucker DIESES Geräts und wandert nicht ins Konto-Profil.
 */

// Etikett-Einstellungen ↔ Geräte-Schlüssel (so wie sie im Konto-Profil stehen)
export function cfgAusSettings(s) {
  return {
    lang: s.kiosk_label_lang === 'zpl' ? 'zpl' : 'cpcl',
    layout: ['nachname', 'eigen', 'grenzpaket'].includes(s.kiosk_label_layout) ? s.kiosk_label_layout : 'hausnummer',
    size: parseLabelSize(s.kiosk_label_size) ? s.kiosk_label_size : '100x150',
    rahmen: s.kiosk_label_rahmen === '1',
    kopf: LABEL_KOEPFE.includes(s.kiosk_label_kopf) ? s.kiosk_label_kopf : 'hausnummer',
    zeilen: parseLabelZeilen(s.kiosk_label_zeilen),
    dienst: s.kiosk_label_dienst === '1',
    rand: parseLabelRand(s.kiosk_label_rand),
    vintedNr: s.kiosk_vinted_sort === '1',
    code: parseLabelCode(s.kiosk_label_code),
    gruss: parseLabelGruss(s.kiosk_label_gruss || ''),
    grafik: parseLabelGrafik(s.kiosk_label_grafik || 'keine'),
    qrUrl: parseLabelQrUrl(s.kiosk_label_qr_url || ''),
    qrText: parseLabelQrText(s.kiosk_label_qr_text || ''),
  };
}
export function settingsAusCfg(cfg) {
  return {
    kiosk_label_lang: cfg.lang === 'zpl' ? 'zpl' : 'cpcl',
    kiosk_label_layout: cfg.layout,
    kiosk_label_size: cfg.size,
    kiosk_label_rahmen: cfg.rahmen ? '1' : '0',
    kiosk_label_kopf: cfg.kopf,
    kiosk_label_zeilen: formatLabelZeilen(cfg.zeilen),
    kiosk_label_dienst: cfg.dienst ? '1' : '0',
    kiosk_label_rand: formatLabelRand(cfg.rand),
    kiosk_label_code: formatLabelCode(cfg.code),
    kiosk_label_gruss: parseLabelGruss(cfg.gruss || ''),
    kiosk_label_grafik: formatLabelGrafik(cfg.grafik),
    kiosk_label_qr_url: parseLabelQrUrl(cfg.qrUrl || ''),
    kiosk_label_qr_text: parseLabelQrText(cfg.qrText || ''),
  };
}

const FESTE = ['100x150', '100x75', 'endlos'];

export default function LabelDesigner({ value, onChange, t, colors, nachnameErlaubt }) {
  const dark = colors?.darkMode;
  const farbe = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', feld: dark ? '#1b1f27' : '#fff' };
  const zpl = value.lang === 'zpl';
  const eigeneGroesse = !FESTE.includes(value.size);
  const teile = /^(\d{2,3})x(\d{1,3})$/.exec(value.size) || [];
  // Eingabefelder für die eigene Größe: eigener Zustand, damit man frei tippen kann (auch Ungültiges)
  const [eigen, setEigen] = useState(eigeneGroesse);
  const [wmm, setWmm] = useState(eigeneGroesse ? teile[1] : '100');
  const [hmm, setHmm] = useState(eigeneGroesse && teile[2] !== '0' ? teile[2] : '50');
  const [endlos, setEndlos] = useState(eigeneGroesse && teile[2] === '0');
  // Zustand für eigenes hochgeladenes Bild (damit Stil/Größe/Kontrast nachträglich verstellt werden können)
  const dateiRef = useRef(null);
  const [rohBild, setRohBild] = useState(null);
  const [bildOpts, setBildOpts] = useState({ stil: 'strich', groesse: 'mittel', schwelle: 150, invert: false });
  const [promptKopiert, setPromptKopiert] = useState(false);

  const tr = (key, fallback) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };

  const set = (patch) => onChange({ ...value, ...patch });
  // 🔀 Zeilen des eigenen Layouts: angehakte in gewählter Reihenfolge oben, danach die übrigen
  const koerperAlle = LABEL_ZEILEN.filter((z) => !LABEL_FUSS.includes(z) && z !== value.kopf);
  const gewaehlt = value.zeilen.filter((z) => koerperAlle.includes(z));
  const koerper = [...gewaehlt, ...koerperAlle.filter((z) => !gewaehlt.includes(z))];
  const umschalten = (z) => set({ zeilen: value.zeilen.includes(z) ? value.zeilen.filter((x) => x !== z) : [...value.zeilen, z] });
  const schiebe = (z, richtung) => {
    const liste = [...gewaehlt];
    const i = liste.indexOf(z), j = i + richtung;
    if (i < 0 || j < 0 || j >= liste.length) return;
    [liste[i], liste[j]] = [liste[j], liste[i]];
    set({ zeilen: [...liste, ...value.zeilen.filter((x) => !koerperAlle.includes(x))] });
  };
  // 📐 Ränder: gleichen aus, was dieser Drucker oben/unten/seitlich nicht bedruckt
  const rand = value.rand || { oben: 0, unten: 0, seite: 0 };
  const [randOffen, setRandOffen] = useState(!!(rand.oben || rand.unten || rand.seite));
  const setRand = (k, v) => set({ rand: parseLabelRand(formatLabelRand({ ...rand, [k]: v })) });
  const eigeneSetzen = (w, h, e) => {
    setWmm(w); setHmm(h); setEndlos(e);
    const key = `${parseInt(w, 10) || 0}x${e ? 0 : (parseInt(h, 10) || 0)}`;
    if (parseLabelSize(key)) set({ size: key });
  };
  const eigenerKey = `${parseInt(wmm, 10) || 0}x${endlos ? 0 : (parseInt(hmm, 10) || 0)}`;
  const eigenUngueltig = eigen && !parseLabelSize(eigenerKey);

  const chip = (an) => ({
    padding: '9px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    border: `2px solid ${an ? '#1976d2' : farbe.rand}`, background: an ? '#1976d2' : 'transparent', color: an ? '#fff' : farbe.text,
  });
  const titel = { fontSize: 13, fontWeight: 700, margin: '14px 0 6px' };
  const eingabe = { width: 64, padding: '8px', borderRadius: 8, border: `1px solid ${farbe.rand}`, background: farbe.feld, color: farbe.text, fontSize: 15, textAlign: 'center' };
  const haken = (id, an, onClick, text) => (
    <label key={id} data-testid={`label-${id}`} data-on={an ? '1' : '0'} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, padding: '5px 0', cursor: 'pointer' }}>
      <input type="checkbox" checked={!!an} onChange={onClick} style={{ width: 18, height: 18 }} />
      <span>{text}</span>
    </label>
  );

  const beispiel = { ...beispielPaket(value.layout), paketdienst: 'DHL' };
  // 💬 Eigener Grußtext unter dem Kasten (Wunsch Folkert 15.10.2026)
  const gruss = value.gruss || '';
  const grussBlock = () => (
    <>
      <div style={titel}>💬 {tr('label.greeting', 'Eigener Grußtext unterm Etikett')}</div>
      <div style={{ fontSize: 12, color: farbe.sub, marginBottom: 6, lineHeight: 1.45 }}>
        {tr('label.greetingHint', 'Optionaler kurzer Text unter dem Kasten (z. B. „Have a nice day! The Green GOAT“).')}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          data-testid="label-gruss"
          type="text"
          maxLength={LABEL_GRUSS_MAX}
          value={gruss}
          placeholder={tr('label.greetingPlaceholder', 'Have a nice day! The Green GOAT')}
          onChange={(e) => set({ gruss: parseLabelGruss(e.target.value) })}
          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: `1px solid ${farbe.rand}`, background: farbe.feld, color: farbe.text, fontSize: 14 }}
        />
        {gruss && (
          <button type="button" data-testid="label-gruss-clear" onClick={() => set({ gruss: '' })}
            style={{ ...chip(false), padding: '8px 11px' }} aria-label="Clear">✕</button>
        )}
      </div>
    </>
  );

  // 🖼️ Saison-Motiv oder eigenes Bild (ChatGPT / Logo) als 1-Bit-Grafik
  const grafik = value.grafik || { art: 'keine', bpr: 0, h: 0, hex: '' };
  const bildNeuBerechnen = async (quelle, neueOpts) => {
    try {
      const g = await bildZuLabelGrafik(quelle, neueOpts);
      set({ grafik: g });
    } catch { /* */ }
  };
  const dateiGewaehlt = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = String(r.result || '');
      setRohBild(dataUrl);
      bildNeuBerechnen(dataUrl, bildOpts);
    };
    r.readAsDataURL(file);
    e.target.value = '';
  };
  const optsAendern = (patch) => {
    const neu = { ...bildOpts, ...patch };
    setBildOpts(neu);
    if (rohBild) bildNeuBerechnen(rohBild, neu);
  };
  const chatGptPromptKopieren = async () => {
    const promptText = tr(
      'label.chatGptPromptText',
      'Erstelle ein breites, flaches Schwarz-Weiß-Banner (Seitenverhältnis 4:1, weißer Hintergrund, dicke klare schwarze Linien ohne Graustufen, Stempel-/Linolschnitt-Stil für einen Thermodrucker) passend für ein Paketshop-Etikett zum Thema: [z. B. Weihnachten / Halloween / Unser Shop-Maskottchen].'
    );
    try {
      await navigator.clipboard.writeText(promptText);
      setPromptKopiert(true);
      setTimeout(() => setPromptKopiert(false), 4000);
    } catch { /* */ }
  };

  const themenNamen = {
    keine: tr('label.theme_keine', 'Kein Bild'),
    halloween: `🎃 ${tr('label.theme_halloween', 'Halloween')}`,
    weihnachten: `🎄 ${tr('label.theme_weihnachten', 'Weihnachten')}`,
    festtage: `✨ ${tr('label.theme_festtage', 'Festtage')}`,
    herz: `💛 ${tr('label.theme_herz', 'Danke / Smile')}`,
  };

  const grafikBlock = () => (
    <>
      <div style={titel}>🖼️ {tr('label.graphic', 'Motiv / Eigenes Bild (z. B. Weihnachten, Halloween, ChatGPT)')}</div>
      <div style={{ fontSize: 12, color: farbe.sub, marginBottom: 6, lineHeight: 1.45 }}>
        {tr('label.graphicHint', 'Wähle ein Saison-Motiv oder lade ein eigenes Bild/Logo hoch — es wird automatisch für den Thermodrucker in Schwarz-Weiß umgewandelt.')}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {LABEL_THEMEN.map((th) => (
          <button key={th} type="button" data-testid={`label-theme-${th}`} aria-pressed={grafik.art === th} style={chip(grafik.art === th)}
            onClick={() => set({ grafik: parseLabelGrafik(th === 'keine' ? 'keine' : `preset:${th}`) })}>
            {themenNamen[th]}
          </button>
        ))}
        <button type="button" data-testid="label-theme-eigen" aria-pressed={grafik.art === 'eigen'} style={chip(grafik.art === 'eigen')}
          onClick={() => dateiRef.current && dateiRef.current.click()}>
          📤 {tr('label.graphicUpload', 'Eigenes Bild hochladen')}
        </button>
        <input ref={dateiRef} data-testid="label-grafik-file" type="file" accept="image/*" onChange={dateiGewaehlt} style={{ display: 'none' }} />
      </div>

      <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" data-testid="label-chatgpt-prompt" onClick={chatGptPromptKopieren}
          style={{ ...chip(false), padding: '6px 10px', fontSize: 12, borderStyle: 'dashed' }}>
          💡 {promptKopiert ? tr('label.chatGptCopied', 'ChatGPT-Vorlage kopiert!') : tr('label.chatGptCopy', 'ChatGPT-Vorlage kopieren')}
        </button>
        {grafik.art === 'eigen' && (
          <button type="button" data-testid="label-grafik-reupload" onClick={() => dateiRef.current && dateiRef.current.click()}
            style={{ ...chip(false), padding: '6px 10px', fontSize: 12 }}>
            🔄 {tr('label.graphicChange', 'Anderes Bild wählen')}
          </button>
        )}
      </div>

      {grafik.art === 'eigen' && rohBild && (
        <div data-testid="label-grafik-opts" style={{ marginTop: 8, padding: 10, borderRadius: 8, border: `1px solid ${farbe.rand}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: farbe.sub, minWidth: 58 }}>{tr('label.graphicStyle', 'Stil')}:</span>
            <button type="button" data-testid="label-grafik-stil-strich" style={chip(bildOpts.stil === 'strich')}
              onClick={() => optsAendern({ stil: 'strich' })}>✏️ {tr('label.graphicStyleLine', 'Strich / Logo')}</button>
            <button type="button" data-testid="label-grafik-stil-raster" style={chip(bildOpts.stil === 'raster')}
              onClick={() => optsAendern({ stil: 'raster' })}>📷 {tr('label.graphicStyleDither', 'Foto-Raster')}</button>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: farbe.sub, minWidth: 58 }}>{tr('label.codeSize', 'Größe')}:</span>
            {['klein', 'mittel', 'gross'].map((g) => (
              <button key={g} type="button" data-testid={`label-grafik-size-${g}`} style={chip(bildOpts.groesse === g)}
                onClick={() => optsAendern({ groesse: g })}>{tr(`label.codeSize_${g}`, g)}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: farbe.sub, minWidth: 58 }}>{tr('label.graphicContrast', 'Kontrast')}:</span>
            {[[110, tr('label.contrastLight', 'Heller')], [150, tr('label.contrastNormal', 'Normal')], [190, tr('label.contrastDark', 'Kräftiger')]].map(([sw, lbl]) => (
              <button key={sw} type="button" data-testid={`label-grafik-contrast-${sw}`} style={chip(bildOpts.schwelle === sw)}
                onClick={() => optsAendern({ schwelle: sw })}>{lbl}</button>
            ))}
            <button type="button" data-testid="label-grafik-invert" style={chip(bildOpts.invert)}
              onClick={() => optsAendern({ invert: !bildOpts.invert })}>☯️ {tr('label.graphicInvert', 'Invertieren')}</button>
          </div>
        </div>
      )}
    </>
  );

  // 💝 Untere Etikett-Hälfte: Eigener QR-Code + mehrzeiliger Infotext (Wunsch Folkert 04.11.2026: Crowdfunding / Spende / Website)
  const qrUrl = value.qrUrl || '';
  const qrText = value.qrText || '';
  const appLang = (() => {
    try { return (localStorage.getItem('app_language') || (typeof navigator !== 'undefined' && navigator.language) || 'de').slice(0, 2).toLowerCase(); } catch { return 'de'; }
  })();
  const qrTxt = appLang === 'nl' ? {
    titel: 'Onderste helft: Eigen QR-code & tekst (bijv. crowdfunding / donatie / website)',
    hint: 'Drukt op de vrije onderste helft van het label een grote QR-code (links) en jouw uitleg of oproep (rechts) af.',
    urlLbl: '🔗 Link / URL voor de QR-code:',
    urlPh: 'https://gofund.me/... of https://tikkie.me/...',
    txtLbl: '📝 Tekst naast de QR-code (meerdere regels mogelijk):',
    txtPh: 'Steun The Green GOAT!\nDe vergoedingen van pakketdiensten zijn te laag.\nScan de QR-code voor ons verhaal en een donatie. Dankjewel!',
    clear: 'Onderste helft wissen',
  } : appLang === 'en' ? {
    titel: 'Lower half: Custom QR code & text (e.g. crowdfunding / donation / website)',
    hint: 'Prints a large QR code (left) and your explanation or call for support (right) on the unused lower half of the label.',
    urlLbl: '🔗 Link / URL for the QR code:',
    urlPh: 'https://gofund.me/... or https://...',
    txtLbl: '📝 Text next to the QR code (multi-line):',
    txtPh: 'Support The Green GOAT!\nRewards from parcel carriers are too low to survive.\nScan the QR code to read our story and make a donation. Thank you!',
    clear: 'Clear lower half',
  } : {
    titel: 'Untere Etikett-Hälfte: Eigener QR-Code & Infotext (z. B. Crowdfunding / Spende / Website)',
    hint: 'Druckt auf die freie untere Hälfte des Etiketts einen großen QR-Code (links) und deinen mehrzeiligen Erklärungstext (rechts).',
    urlLbl: '🔗 Link / URL für den QR-Code:',
    urlPh: 'https://gofund.me/... oder https://...',
    txtLbl: '📝 Text neben dem QR-Code (mehrzeilig):',
    txtPh: 'Unterstütze unseren Paketshop!\nScanne den QR-Code für unsere Geschichte und eine kleine Unterstützung. Vielen Dank!',
    clear: 'Untere Hälfte leeren',
  };
  const spendenBlock = () => (
    <>
      <div style={titel}>💝 {tr('label.qrSectionTitle', qrTxt.titel)}</div>
      <div style={{ fontSize: 12, color: farbe.sub, marginBottom: 6, lineHeight: 1.45 }}>{tr('label.qrSectionHint', qrTxt.hint)}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600 }}>{tr('label.qrUrlLabel', qrTxt.urlLbl)}</div>
        <input
          data-testid="label-qr-url"
          type="url"
          maxLength={LABEL_QR_URL_MAX}
          value={qrUrl}
          placeholder={tr('label.qrUrlPlaceholder', qrTxt.urlPh)}
          onChange={(e) => set({ qrUrl: parseLabelQrUrl(e.target.value) })}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${farbe.rand}`, background: farbe.feld, color: farbe.text, fontSize: 14, boxSizing: 'border-box' }}
        />
        <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2 }}>{tr('label.qrTextLabel', qrTxt.txtLbl)}</div>
        <textarea
          data-testid="label-qr-text"
          rows={4}
          maxLength={LABEL_QR_TEXT_MAX}
          value={qrText}
          placeholder={tr('label.qrTextPlaceholder', qrTxt.txtPh)}
          onChange={(e) => set({ qrText: parseLabelQrText(e.target.value) })}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${farbe.rand}`, background: farbe.feld, color: farbe.text, fontSize: 14, lineHeight: 1.4, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
        />
        {(qrUrl || qrText) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" data-testid="label-qr-clear" onClick={() => set({ qrUrl: '', qrText: '' })}
              style={{ ...chip(false), padding: '6px 10px', fontSize: 12 }}>✕ {tr('label.qrClear', qrTxt.clear)}</button>
          </div>
        )}
      </div>
    </>
  );

  // 🔳 Code aufs Etikett (Barcode/QR der Sendungsnummer, Wunsch Tim 10.10.2026) — für beide Druckertypen
  const code = value.code || { art: 'keine', groesse: 'mittel' };
  const codeBlock = () => (
    <>
      <div style={titel}>{t('label.code')}</div>
      <div style={{ fontSize: 12, color: farbe.sub, marginBottom: 6, lineHeight: 1.45 }}>{t('label.codeHint')}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {LABEL_CODE_ARTEN.map((a) => (
          <button key={a} type="button" data-testid={`label-code-${a}`} aria-pressed={code.art === a} style={chip(code.art === a)}
            onClick={() => set({ code: { ...code, art: a } })}>{t(`label.code_${a}`)}</button>
        ))}
      </div>
      {code.art !== 'keine' && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: farbe.sub }}>{t('label.codeSize')}</span>
          {LABEL_CODE_GROESSEN.map((g) => (
            <button key={g} type="button" data-testid={`label-code-size-${g}`} aria-pressed={code.groesse === g} style={chip(code.groesse === g)}
              onClick={() => set({ code: { ...code, groesse: g } })}>{t(`label.codeSize_${g}`)}</button>
          ))}
        </div>
      )}
    </>
  );
  const maß = parseLabelSize(value.size);
  const mmText = maß ? `${Math.round(maß.w / 8.12)} × ${maß.h === null ? '…' : Math.round(maß.h / 8.12)} mm` : '';

  if (!zpl) {
    return (
      <div data-testid="label-designer" style={{ color: farbe.text }}>
        <div style={{ fontSize: 13, color: farbe.sub, lineHeight: 1.5 }}>{t('label.mobileFixed')}</div>
        {haken('dienst', value.dienst, () => set({ dienst: !value.dienst }), t('label.carrierLine'))}
        {grussBlock()}
        {codeBlock()}
      </div>
    );
  }

  return (
    <div data-testid="label-designer" style={{ color: farbe.text }}>
      <div style={{ ...titel, marginTop: 0 }}>{t('label.size')}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[['100x150', t('printer.size150')], ['100x75', t('printer.size75')], ['endlos', t('printer.sizeEndless')]].map(([k, text]) => (
          <button key={k} type="button" data-testid={`label-size-${k}`} aria-pressed={!eigen && value.size === k} style={chip(!eigen && value.size === k)}
            onClick={() => { setEigen(false); set({ size: k }); }}>{text}</button>
        ))}
        <button type="button" data-testid="label-size-custom" aria-pressed={eigen} style={chip(eigen)}
          onClick={() => { setEigen(true); eigeneSetzen(wmm, hmm, endlos); }}>{t('label.sizeCustom')}</button>
      </div>
      {eigen && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 14 }}>
            <input data-testid="label-w" type="text" inputMode="numeric" maxLength={3} value={wmm} aria-label={t('label.width')}
              onChange={(e) => eigeneSetzen(e.target.value.replace(/\D/g, ''), hmm, endlos)} style={eingabe} />
            <span>×</span>
            <input data-testid="label-h" type="text" inputMode="numeric" maxLength={3} value={endlos ? '' : hmm} disabled={endlos} placeholder={endlos ? '…' : ''} aria-label={t('label.height')}
              onChange={(e) => eigeneSetzen(wmm, e.target.value.replace(/\D/g, ''), endlos)} style={{ ...eingabe, opacity: endlos ? 0.5 : 1 }} />
            <span>mm</span>
            <span style={{ color: farbe.sub, fontSize: 12 }}>({t('label.widthHeight')})</span>
          </div>
          {haken('endless', endlos, () => eigeneSetzen(wmm, hmm, !endlos), t('label.customEndless'))}
          {eigenUngueltig && (
            <div data-testid="label-size-error" style={{ color: '#e5484d', fontSize: 12, marginTop: 2 }}>
              ⚠️ {t('label.sizeRange', { wMin: LABEL_MM.wMin, wMax: LABEL_MM.wMax, hMin: LABEL_MM.hMin, hMax: LABEL_MM.hMax })}
            </div>
          )}
        </div>
      )}
      {(value.size === 'endlos' || (eigen && endlos)) && (
        <div style={{ fontSize: 12, color: farbe.sub, marginTop: 6, lineHeight: 1.4 }}>{t('label.endlessHint')}</div>
      )}

      <div style={titel}>{t('label.template')}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" data-testid="label-layout-hausnummer" aria-pressed={value.layout === 'hausnummer'} style={chip(value.layout === 'hausnummer')} onClick={() => set({ layout: 'hausnummer' })}>{t('label.templateStandard')}</button>
        {nachnameErlaubt && (
          <button type="button" data-testid="label-layout-nachname" aria-pressed={value.layout === 'nachname'} style={chip(value.layout === 'nachname')} onClick={() => set({ layout: 'nachname' })}>{t('label.templateSurname')}</button>
        )}
        {(hasCustomFeature('grenzpaket') || value.layout === 'grenzpaket') && (
          <button type="button" data-testid="label-layout-grenzpaket" aria-pressed={value.layout === 'grenzpaket'} style={chip(value.layout === 'grenzpaket')} onClick={() => set({ layout: 'grenzpaket' })}>
            🇨🇭 {tr('label.templateGrenzpaket', 'Grenzpaket (Original-Quittung)')}
          </button>
        )}
        <button type="button" data-testid="label-layout-eigen" aria-pressed={value.layout === 'eigen'} style={chip(value.layout === 'eigen')} onClick={() => set({ layout: 'eigen' })}>{t('label.templateCustom')}</button>
      </div>

      {value.layout === 'hausnummer' && haken('dienst', value.dienst, () => set({ dienst: !value.dienst }), t('label.carrierLine'))}

      {value.layout === 'eigen' && (
        <>
          <div style={titel}>{t('label.head')}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {LABEL_KOEPFE.map((k) => (
              <button key={k} type="button" data-testid={`label-kopf-${k}`} aria-pressed={value.kopf === k} style={chip(value.kopf === k)} onClick={() => set({ kopf: k })}>{t(`label.field.${k}`)}</button>
            ))}
          </div>
          <div style={titel}>{t('label.lines')}</div>
          <div style={{ fontSize: 12, color: farbe.sub, marginBottom: 4 }}>{t('label.linesOrderHint')}</div>
          {koerper.map((z, i) => {
            const an = value.zeilen.includes(z);
            const nr = an ? gewaehlt.indexOf(z) : -1;
            return (
              <div key={z} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ flex: 1 }}>{haken(`zeile-${z}`, an, () => umschalten(z), t(`label.field.${z}`))}</div>
                {an && (
                  <>
                    <button type="button" data-testid={`label-zeile-${z}-up`} disabled={nr <= 0} aria-label={`${t(`label.field.${z}`)} ▲`}
                      onClick={() => schiebe(z, -1)} style={{ ...chip(false), padding: '4px 10px', opacity: nr <= 0 ? 0.35 : 1 }}>▲</button>
                    <button type="button" data-testid={`label-zeile-${z}-down`} disabled={nr >= gewaehlt.length - 1} aria-label={`${t(`label.field.${z}`)} ▼`}
                      onClick={() => schiebe(z, 1)} style={{ ...chip(false), padding: '4px 10px', opacity: nr >= gewaehlt.length - 1 ? 0.35 : 1 }}>▼</button>
                  </>
                )}
              </div>
            );
          })}
          <div style={titel}>{t('label.footer')}</div>
          {LABEL_FUSS.map((z) => haken(`zeile-${z}`, value.zeilen.includes(z), () => umschalten(z), t(`label.field.${z}`)))}
        </>
      )}

      {grussBlock()}
      {grafikBlock()}
      {spendenBlock()}
      {codeBlock()}

      {haken('frame', value.rahmen, () => set({ rahmen: !value.rahmen }), t('printer.frame'))}

      <button type="button" data-testid="label-margins-toggle" aria-expanded={randOffen} onClick={() => setRandOffen(!randOffen)}
        style={{ background: 'none', border: 'none', padding: '8px 0 2px', color: '#1976d2', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
        {randOffen ? '▾' : '▸'} 📐 {t('label.margins')}
      </button>
      {randOffen && (
        <div data-testid="label-margins" style={{ padding: '6px 0 2px' }}>
          {[['oben', t('label.marginTop'), LABEL_RAND.obenMin, LABEL_RAND.obenMax], ['unten', t('label.marginBottom'), 0, LABEL_RAND.untenMax],
            ['seite', t('label.marginSide'), 0, LABEL_RAND.seiteMax]].map(([k, text, min, max]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <span style={{ flex: 1, fontSize: 14 }}>{text}</span>
              <button type="button" data-testid={`label-margin-${k}-minus`} disabled={rand[k] <= min} aria-label={`${text} −`}
                onClick={() => setRand(k, rand[k] - 1)} style={{ ...chip(false), width: 40, padding: '7px 0', opacity: rand[k] <= min ? 0.4 : 1 }}>−</button>
              <span data-testid={`label-margin-${k}`} style={{ minWidth: 56, textAlign: 'center', fontSize: 15, fontWeight: 700 }}>{rand[k]} mm</span>
              <button type="button" data-testid={`label-margin-${k}-plus`} disabled={rand[k] >= max} aria-label={`${text} +`}
                onClick={() => setRand(k, rand[k] + 1)} style={{ ...chip(false), width: 40, padding: '7px 0', opacity: rand[k] >= max ? 0.4 : 1 }}>+</button>
            </div>
          ))}
          <div style={{ fontSize: 12, color: farbe.sub, marginTop: 4, lineHeight: 1.45 }}>{t('label.marginsHint')}</div>
        </div>
      )}

      <div style={titel}>{t('label.preview')}</div>
      <LabelPreview lines={buildZplLines(beispiel, {}, value)} mm={mmText} />
    </div>
  );
}
